/**
 * WEB ERROR REPORTING — the small API the apps call.
 *
 *   installErrorReporting(...)   once, from main.tsx, after the first render
 *   reportError(error)           a caught error worth knowing about
 *   reportAppCheckFailure(kind)  a fixed, data-free App Check signal
 *   reportShapeRefusal(code)     a fixed, data-free "Backend refused our document" signal
 *   reportStripeRedirectRefused() a fixed, data-free "not a Stripe link, not followed" signal
 *   lastSupportEventId()         the most recent event id (kept, NOT shown in rc.1)
 *
 * This module never imports the SDK. The caller hands it a `load` function:
 * both apps pass a dynamic import, so the SDK is its own chunk, fetched in
 * idle time and only on a host that may report (Book since the release after
 * v1.0.0-rc.3: it is the one chunk Book's one-script contract allows).
 *
 * EVERYTHING HERE FAILS SHUT AND QUIET. If the gate says no, nothing loads.
 * If the SDK fails to load or throws while starting, reporting stays off and
 * the page carries on — an error reporter that can break the page it reports
 * on is worse than none.
 *
 * EARLY EVENTS ARE QUEUED, NEVER LOST (Web Director, the release after
 * v1.0.0-rc.3). Anything reported while the SDK chunk is still loading —
 * reportError, the fixed-message signals, and uncaught errors and rejections
 * (held by two plain listeners) — waits in `early` and is delivered once the
 * client starts, through the same per-page budget (each kind once, at most
 * ten events). A repeat of something already queued holds no slot (see
 * `earlyKey`), so a burst of one error cannot crowd out another kind, and the
 * fixed-message signals, at most one each by their own flags, always fit. If
 * the chunk never loads, reporting is off for the page and the queue is
 * dropped with it. An error thrown before
 * `installErrorReporting` runs at all — i.e. during the very first render —
 * is not seen by these listeners; the router's error element still reports
 * route errors once reporting starts.
 */
import { reportingDsn, reportingSends, type ReportingSurface } from "./config";
import type { BuildIdentity } from "./event";
import type { PageLocation } from "./scrub";
import type { StartedClient, StartOptions } from "./sentryClient";

export type { ReportingSurface } from "./config";

export interface ClientModule {
  startSentryClient(options: StartOptions): StartedClient;
}

export interface InstallOptions {
  readonly surface: ReportingSurface;
  readonly env: string;
  readonly dsn: string;
  readonly commit: string;
  readonly load: () => Promise<ClientModule>;
  /** Defaults to the real page; tests pass their own. */
  readonly location?: () => PageLocation;
  readonly transport?: StartOptions["transport"];
  /** Defaults to idle time. Tests pass an immediate scheduler. */
  readonly schedule?: (run: () => void) => void;
}

/** App Check failure kinds. Constants only: the event carries no dynamic text. */
export const APP_CHECK_FAILURE = {
  unavailable: "unavailable",
  timeout: "timeout",
  refused: "refused",
} as const;
export type AppCheckFailureKind = (typeof APP_CHECK_FAILURE)[keyof typeof APP_CHECK_FAILURE];

/**
 * Distinct early reports held before the client starts. Generous on purpose:
 * the budget (budget.ts, ten events, each kind once) decides what is SENT;
 * this only bounds memory while the chunk loads.
 */
const MAX_EARLY = 50;

type Deliver = (client: StartedClient) => void;

type State =
  | { readonly phase: "off" }
  | { readonly phase: "pending"; readonly early: Map<string, Deliver>; readonly detach: () => void }
  | { readonly phase: "on"; readonly client: StartedClient };

let state: State = { phase: "off" };
// A chunk that failed to load is not fetched again on this page (no retry
// storm): a later install is a no-op until the next page load.
let loadFailed = false;
let lastEventId: string | null = null;
let reported = new WeakSet<object>();
// At most ONE App Check event per page load, whatever the kinds (Web
// Director, rc.1): a visitor whose reCAPTCHA is blocked must not cost more
// than one event. The first classified failure is the one reported.
let appCheckReported = false;
const shapeReported = new Set<string>();
let stripeRedirectReported = false;
let signOutFailureReported = false;
let deliberateSignOutFailureReported = false;
let replayConflictReported = false;

function currentLocation(): PageLocation {
  const { origin, pathname, search, hash } = window.location;
  return { origin, pathname, search, hash };
}

function idle(run: () => void): void {
  const requestIdle = (globalThis as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;
  if (typeof requestIdle === "function") requestIdle(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

let earlySerial = 0;
const NATIVE_ERRORS: ReadonlyArray<unknown> = [Error, TypeError, RangeError, ReferenceError, SyntaxError, URIError, EvalError];

/**
 * An early report's queue key. Two reports with the same key are certain to
 * be the same KIND once built (budget.ts `eventKind` folds more, never less):
 * the same string, or native errors with the same name and message down the
 * same `cause` chain (all early events are built together, at delivery, so
 * at one route). Anything else gets a key of its own, because its kind may
 * come from more than its text: an Apollo error is reported by its codes or
 * HTTP status (event.ts), an aggregate by its members. So nothing that might
 * be a new kind is merged away.
 */
function earlyKey(value: unknown): string {
  if (typeof value === "string") return `s:${value}`;
  let key = "e";
  for (let link: unknown = value, depth = 0; link !== undefined; link = (link as Error).cause, depth++) {
    if (depth > 8 || !(link instanceof Error) || !NATIVE_ERRORS.includes(link.constructor)) return `u:${earlySerial++}`;
    key += `\n${link.name}:${link.message}`;
  }
  return key;
}

// Digit-folded keys already queued: a report of a new SHAPE is queued even
// when the queue is full, so copies that differ only in a number ("after
// 812ms", "after 9034ms": one kind, budget.ts) cannot crowd out another kind.
const earlyShapes = new Set<string>();

/**
 * Queue an early report unless one with the same key is already waiting.
 * `always` is for the fixed-message signals: their flags allow one each per
 * page, and a flag already set must not stand for an event that was dropped.
 */
function holdEarly(early: Map<string, Deliver>, key: string, deliver: Deliver, always = false): void {
  if (early.has(key)) return;
  const shape = key[0] === "u" ? key : key.replace(/\d+/g, "#");
  if (always || early.size < MAX_EARLY || (!earlyShapes.has(shape) && earlyShapes.size < MAX_EARLY)) {
    early.set(key, deliver);
    earlyShapes.add(shape);
  }
}

/**
 * LAYER 1 OF AN INERT ENVIRONMENT (qa, config.ts REPORTING_SENDS): a transport
 * that accepts every envelope and sends none. It never calls fetch,
 * sendBeacon or XHR, so the reporter runs as on Prod and nothing leaves the
 * page. It overrides any transport the caller passed.
 */
export const INERT_TRANSPORT: NonNullable<StartOptions["transport"]> = () => ({
  send: () => Promise.resolve({}),
  flush: () => Promise.resolve(true),
});

/** The release name a build reports as. */
export function releaseFor(surface: ReportingSurface, commit: string): string {
  return `lura-${surface}-web@${commit}`;
}

export function installErrorReporting(options: InstallOptions): void {
  try {
    if (state.phase !== "off" || loadFailed || typeof window === "undefined") return;
    const location = options.location ?? currentLocation;
    const dsn = reportingDsn(options.surface, options.env, options.dsn, location().origin ? new URL(location().origin).hostname : "");
    if (dsn === null) return;

    const early = new Map<string, Deliver>();
    const hold = (value: unknown) => holdEarly(early, earlyKey(value), (client) => send(client, value, true));
    const onError = (event: ErrorEvent) => hold(event.error ?? event.message);
    const onRejection = (event: PromiseRejectionEvent) => hold(event.reason);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    const detach = () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
    state = { phase: "pending", early, detach };

    const identity: BuildIdentity = {
      surface: options.surface,
      env: options.env,
      release: releaseFor(options.surface, options.commit),
      build: options.commit.slice(0, 12),
    };

    (options.schedule ?? idle)(() => {
      options
        .load()
        .then((module) => {
          if (state.phase !== "pending") return;
          const pending = state;
          pending.detach();
          // Layer 1: an environment that must not send gets the inert
          // transport, whatever the caller passed (config.ts, INERT).
          const transport = reportingSends(identity.env) ? options.transport : INERT_TRANSPORT;
          const client = module.startSentryClient({ dsn, identity, location, transport });
          state = { phase: "on", client };
          earlyShapes.clear();
          for (const deliver of pending.early.values()) deliver(client);
        })
        .catch(() => {
          // The SDK chunk failed to load (offline, a stale deploy) or threw
          // while starting. Reporting stays off; the page is unaffected.
          if (state.phase === "pending") state.detach();
          state = { phase: "off" };
          loadFailed = true;
          earlyShapes.clear();
        });
    });
  } catch {
    state = { phase: "off" };
  }
}

function send(client: StartedClient, error: unknown, early = false): void {
  try {
    lastEventId = client.captureException(error, early);
  } catch {
    // Reporting must never become the error.
  }
}

/** Report a caught error. A no-op when reporting is off. Never throws. */
export function reportError(error: unknown): void {
  try {
    if (typeof error === "object" && error !== null) {
      if (reported.has(error)) return;
      reported.add(error);
    }
    if (state.phase === "pending") {
      holdEarly(state.early, earlyKey(error), (client) => send(client, error));
      return;
    }
    if (state.phase !== "on") return;
    send(state.client, error);
  } catch {
    // Reporting must never become the error.
  }
}

/**
 * A fixed-message App Check signal (`app_check_failure:<kind>`, level warning),
 * at most once per page load. Never the SDK's or a third party's error text.
 */
export function reportAppCheckFailure(kind: AppCheckFailureKind): void {
  try {
    if (state.phase === "off" || appCheckReported) return;
    appCheckReported = true;
    const deliver = (client: StartedClient) => {
      lastEventId = client.captureMessage(`app_check_failure:${kind}`);
    };
    if (state.phase === "on") deliver(state.client);
    else holdEarly(state.early, `m:app_check_failure:${kind}`, deliver, true);
  } catch {
    // As above.
  }
}

/** The two shape-refusal codes. Constants only: the event carries no dynamic text. */
export type ShapeRefusalCode = "GRAPHQL_VALIDATION_FAILED" | "GRAPHQL_PARSE_FAILED";

/**
 * Backend refused a request DOCUMENT (HTTP 400, GRAPHQL_VALIDATION_FAILED or
 * GRAPHQL_PARSE_FAILED): a client bug, never retried. A fixed message,
 * `graphql_shape_refused:<code>` at warning level, and nothing else: never the
 * document, its variables, the operation name or Backend's message. At most
 * once per code per page load (the send budget also drops a repeat), so a
 * render loop re-sending the same broken document costs one event.
 */
export function reportShapeRefusal(code: ShapeRefusalCode): void {
  try {
    if (state.phase === "off" || shapeReported.has(code)) return;
    shapeReported.add(code);
    const message = code === "GRAPHQL_PARSE_FAILED"
      ? "graphql_shape_refused:GRAPHQL_PARSE_FAILED"
      : "graphql_shape_refused:GRAPHQL_VALIDATION_FAILED";
    const deliver = (client: StartedClient) => {
      lastEventId = client.captureMessage(message);
    };
    if (state.phase === "on") deliver(state.client);
    else holdEarly(state.early, `m:${message}`, deliver, true);
  } catch {
    // Reporting must never become the error.
  }
}

/**
 * apps/app refused to leave for a Stripe Connect link that failed its exact
 * host allowlist (features/stripeConnect/redirect.ts). A fixed message,
 * `stripe_redirect_refused` at warning level, and nothing else: the refused
 * string may be a bearer Account Link, so it is never passed here at all. At
 * most once per page load, however many presses of Try again.
 */
export function reportStripeRedirectRefused(): void {
  try {
    if (state.phase === "off" || stripeRedirectReported) return;
    stripeRedirectReported = true;
    const deliver = (client: StartedClient) => {
      lastEventId = client.captureMessage("stripe_redirect_refused");
    };
    if (state.phase === "on") deliver(state.client);
    else holdEarly(state.early, "m:stripe_redirect_refused", deliver, true);
  } catch {
    // Reporting must never become the error.
  }
}

/**
 * Book could not sign the person out even with the tab visible, after a
 * session end, and fell back to its one safety reload
 * (apps/book/src/auth/signOutSafely.ts). A fixed message,
 * `session_end_signout_failed`, and nothing else: no uid, no Firebase error
 * text. At most once per page load. A hidden tab that recovers on its next
 * visible moment is not reported: that is the Firebase SDK's normal
 * behaviour, not a fault.
 */
export function reportSignOutFailed(): void {
  try {
    if (state.phase === "off" || signOutFailureReported) return;
    signOutFailureReported = true;
    const deliver = (client: StartedClient) => {
      lastEventId = client.captureMessage("session_end_signout_failed");
    };
    if (state.phase === "on") deliver(state.client);
    else holdEarly(state.early, "m:session_end_signout_failed", deliver, true);
  } catch {
    // Reporting must never become the error.
  }
}

/**
 * The person pressed Sign out in Book and Firebase refused it before and
 * after the one reload (apps/book/src/auth/deliberateSignOut.ts), so they
 * stay signed in, visibly. A fixed message, `deliberate_signout_failed`, and
 * nothing else: no uid, no Firebase error text. At most once per page load.
 */
export function reportDeliberateSignOutFailed(): void {
  try {
    if (state.phase === "off" || deliberateSignOutFailureReported) return;
    deliberateSignOutFailureReported = true;
    const deliver = (client: StartedClient) => {
      lastEventId = client.captureMessage("deliberate_signout_failed");
    };
    if (state.phase === "on") deliver(state.client);
    else holdEarly(state.early, "m:deliberate_signout_failed", deliver, true);
  } catch {
    // Reporting must never become the error.
  }
}

/**
 * A guest booking's Check, an EXACT replay of the original request, was
 * answered IDEMPOTENCY_CONFLICT, which Backend calls a defect (rc.11 1c). A
 * fixed message, `booking_replay_conflict:IDEMPOTENCY_CONFLICT`, and nothing
 * else: never the key, the request or the uid. At most once per page load.
 */
export function reportReplayConflict(): void {
  try {
    if (state.phase === "off" || replayConflictReported) return;
    replayConflictReported = true;
    const deliver = (client: StartedClient) => {
      lastEventId = client.captureMessage("booking_replay_conflict:IDEMPOTENCY_CONFLICT");
    };
    if (state.phase === "on") deliver(state.client);
    else holdEarly(state.early, "m:booking_replay_conflict:IDEMPOTENCY_CONFLICT", deliver, true);
  } catch {
    // Reporting must never become the error.
  }
}

/** The last event id this page sent, for a future support reference. Not displayed in rc.1. */
export function lastSupportEventId(): string | null {
  return lastEventId;
}

/** Test-only: forget everything this module holds. */
export function resetErrorReportingForTests(): void {
  if (state.phase === "pending") state.detach();
  state = { phase: "off" };
  loadFailed = false;
  earlyShapes.clear();
  lastEventId = null;
  reported = new WeakSet<object>();
  appCheckReported = false;
  shapeReported.clear();
  stripeRedirectReported = false;
  signOutFailureReported = false;
  deliberateSignOutFailureReported = false;
  replayConflictReported = false;
}
